import { useEffect, useState } from "react";
import MiniCalendar from "components/calendar/MiniCalendar";
import WeeklyRevenue from "views/admin/default/components/WeeklyRevenue";
import TotalSpent from "views/admin/default/components/TotalSpent";
import PieChartCard from "views/admin/default/components/PieChartCard";
import { IoDocuments } from "react-icons/io5";
import { MdBarChart, MdDashboard, MdPeople, MdDeliveryDining } from "react-icons/md";

import Widget from "components/widget/Widget";
import DailyTraffic from "views/admin/default/components/DailyTraffic";
import TaskCard from "views/admin/default/components/TaskCard";
import api from "utils/api";
import { useAuth } from "context/AuthContext";

const POLL_INTERVAL_MS = 15000; // refresh every 15 seconds for realtime feel

const Dashboard = () => {
  const { role } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    if (role !== "SA" && role !== "A") return;
    try {
      const res = await api.get("/admin/dashboard_stats");
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load dashboard stats:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch + polling for realtime updates
  useEffect(() => {
    fetchStats();
    if (role !== "SA" && role !== "A") return;
    const interval = setInterval(fetchStats, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [role]);

  if (role === "R") {
    // Runner boys see a simplified dashboard
    return (
      <div className="mt-3 min-w-0 w-full">
        <div className="rounded-xl bg-white p-4 sm:p-6 shadow-md dark:bg-navy-800">
          <h2 className="text-xl sm:text-2xl font-bold text-navy-700 dark:text-white">
            Welcome, Runner
          </h2>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
            View your assigned orders from the Bookings page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 w-full">
      {/* Realtime indicator */}
      {(role === "SA" || role === "A") && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          <span className="inline-flex h-2 w-2 rounded-full bg-green-500 animate-pulse shrink-0" aria-hidden />
          <span>Live data</span>
          <span className="text-gray-400 dark:text-gray-500 hidden sm:inline">· Refreshes every 15s</span>
        </div>
      )}
      {/* Card widgets - items-start so cards don't stretch to row height */}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 2xl:grid-cols-3 3xl:grid-cols-5 items-start">
        <Widget
          icon={<MdBarChart className="h-7 w-7" />}
          title={"Total Orders"}
          subtitle={loading ? "..." : String(stats?.total_orders ?? 0)}
        />
        <Widget
          icon={<IoDocuments className="h-6 w-6" />}
          title={"Pending Orders"}
          subtitle={loading ? "..." : String(stats?.pending_orders ?? 0)}
        />
        <Widget
          icon={<MdDashboard className="h-6 w-6" />}
          title={"Total Revenue"}
          subtitle={loading ? "..." : `₹${(stats?.total_revenue ?? 0).toLocaleString()}`}
        />
        <Widget
          icon={<MdPeople className="h-7 w-7" />}
          title={"Active Customers"}
          subtitle={loading ? "..." : String(stats?.active_customers ?? 0)}
        />
        <Widget
          icon={<MdDeliveryDining className="h-6 w-6" />}
          title={"Active Runners"}
          subtitle={loading ? "..." : String(stats?.active_runners ?? 0)}
        />
      </div>

      {/* Calendar only — charts/tables disabled
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <div className="grid grid-cols-1 rounded-[20px] max-w-md">
          <MiniCalendar />
        </div>
      </div>*/}

      {/* Charts row - items-start keeps card heights from stretching */}
      <div className="mt-4 sm:mt-5 grid grid-cols-1 gap-3 sm:gap-5 md:grid-cols-2 min-w-0 items-start">
        <TotalSpent />
        <WeeklyRevenue />
        <DailyTraffic />
        <div className="grid grid-cols-1 gap-3 sm:gap-5 rounded-[20px] md:grid-cols-2 min-w-0 items-start">

          <PieChartCard />
          <MiniCalendar />
        </div>


      </div>


    </div>
  );
};

export default Dashboard;
